import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({ compact = false }) => {
  const { t, languageName, changeLanguage, getAvailableLanguages, isLoading, setContextLanguages } = useLanguage();
  const { makeAuthenticatedRequest, setSettings, uiLanguages } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);

  const availableLanguages = getAvailableLanguages();

  // Sprachen vom Backend laden — nur beim Mount.
  // makeAuthenticatedRequest gibt pre-parsed JSON zurück (kein .json()-Aufruf!).
  // Die API-Basis-URL wird automatisch von makeAuthenticatedRequest ergänzt.
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!makeAuthenticatedRequest) return;
      try {
        setIsApiLoading(true);
        // Gibt bereits geparsten JSON zurück — KEIN .json() aufrufen
        const userData = await makeAuthenticatedRequest('/user');

        if (userData?.available_languages?.length > 0) {
          localStorage.setItem('hejvi_available_languages', JSON.stringify(userData.available_languages));
          if (setContextLanguages) {
            setContextLanguages(userData.available_languages);
          }
        }

        // Bevorzugte Sprache des Nutzers aktivieren
        const preferredId = userData?.user?.preferred_language ?? userData?.data?.preferred_language;
        if (preferredId && userData?.available_languages) {
          const langObj = userData.available_languages.find(
            l => Number(l.id) === Number(preferredId)
          );
          if (langObj?.language_code) {
            await changeLanguage(langObj.language_code);
          }
        }
      } catch (err) {
        console.error('LanguageSwitcher: Sprachen konnten nicht geladen werden:', err);
      } finally {
        setIsApiLoading(false);
      }
    };

    fetchLanguages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to get language code from API ID
  const getLanguageCodeFromId = (id) => {
    const languageMap = {
      1: 'de', // German
      2: 'en', // English
      3: 'ru', // Russian
      4: 'ar', // Arabic
      5: 'fr'  // French
    };
    return languageMap[id];
  };

  // --- ÄNDERUNG 1: Neue Logik mit Event-Handling für Mobile ---
  const handleLanguageChange = async (e, languageObj) => {
    // Wichtig für iPhone: Verhindert, dass Klicks "durchfallen" oder das Menü sofort schließen
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      // 1. ZUERST Datenbank-Update senden (Feuer & Vergessen Prinzip)
      // Wir machen das vor dem UI-Wechsel, damit der Request sicher rausgeht.
      if (setSettings && languageObj.id) {
        console.log('Sende Sprache an Backend ID:', languageObj.id);
        // Wir warten nicht auf das Ergebnis (kein await), damit die UI schnell bleibt,
        // aber der Request wird angestoßen.
        setSettings({ language: languageObj.id }).catch(err => 
          console.error('Backend save failed:', err)
        );
      }

      // 2. DANN Lokal ändern
      // Wir nutzen direkt den Code aus dem Objekt (sicherer als Name)
      await changeLanguage(languageObj.code || languageObj.name);
      
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const currentLanguage = availableLanguages.find(lang => lang.name === languageName);

  // Helper function to render flag (image or emoji)
  const renderFlag = (flag) => {
    if (flag && flag.startsWith('http')) {
      // It's a URL, render as image with fallback
      return (
        <span className="language-flag">
          <img 
            src={flag} 
            alt="flag" 
            className="language-flag-image"
            onError={(e) => {
              // Hide the image and show fallback emoji
              e.target.style.display = 'none';
              const fallback = e.target.parentElement.querySelector('.language-flag-emoji');
              if (fallback) {
                fallback.style.display = 'inline-block';
              }
            }}
          />
          <span className="language-flag-emoji" style={{ display: 'none' }}>
            {getLanguageFlagFromName(flag) || '🌐'}
          </span>
        </span>
      );
    }
    // It's an emoji, render as text
    return (
      <span className="language-flag">
        <span className="language-flag-emoji">{flag || '🌐'}</span>
      </span>
    );
  };

  // Helper function to get emoji flag from URL
  const getLanguageFlagFromName = (url) => {
    if (url.includes('/de.png')) return '🇩🇪';
    if (url.includes('/gb.png')) return '🇺🇸';
    if (url.includes('/ru.png')) return '🇷🇺';
    if (url.includes('/ar.png')) return '🇸🇦';
    if (url.includes('/fr.png')) return '🇫🇷';
    return '🌐';
  };

  if (compact) {
    return (
      <div className="language-switcher-compact">
        <button 
          className="language-button-compact"
          onClick={() => setIsOpen(!isOpen)}
          title={t('language')}
          disabled={isLoading || isApiLoading}
        >
          {isLoading || isApiLoading ? (
            <div className="language-loading-spinner"></div>
          ) : (
            renderFlag(currentLanguage?.flag)
          )}
        </button>
        
        {isOpen && (
          <div className="language-dropdown-compact">
            {availableLanguages.map((language) => (
              <button
                key={language.id || language.code}
                className={`language-option-compact ${language.name === languageName ? 'active' : ''}`}
                onClick={(e) => handleLanguageChange(e, language)}
                disabled={isLoading || isApiLoading}
              >
                {renderFlag(language.flag)}
                <span className="language-name">{language.name}</span>
                {(isLoading || isApiLoading) && language.name === languageName && (
                  <div className="language-loading-spinner-small"></div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="language-switcher">
      <button 
        className="language-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading || isApiLoading}
      >
        {isLoading || isApiLoading ? (
          <div className="language-loading-spinner"></div>
        ) : (
          <>
            {renderFlag(currentLanguage?.flag)}
            <span className="language-name">
              {currentLanguage?.name || 'English'}
            </span>
          </>
        )}
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`} 
          width="12" 
          height="8" 
          viewBox="0 0 12 8" 
          fill="none"
        >
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {isOpen && (
        <div className="language-dropdown">
          {availableLanguages.map((language) => (
            <button
              key={language.id || language.code}
              className={`language-option ${language.name === languageName ? 'active' : ''}`}
              onClick={(e) => handleLanguageChange(e, language)}
              disabled={isLoading || isApiLoading}
            >
              {renderFlag(language.flag)}
              <span className="language-name">{language.name}</span>
              {(isLoading || isApiLoading) && language.name === languageName && (
                <div className="language-loading-spinner-small"></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

